/**
 * Theme model — light/dark/system modes backed by the design tokens.
 *
 * The active mode is persisted to localStorage (`atlas-theme`) and applied
 * as a `data-theme` attribute on <html>. tokens.css defines the dark
 * overrides under `[data-theme='dark']`; everything else reads the light
 * defaults from `:root`.
 */
export type ThemeMode = "light" | "dark" | "system";

export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "atlas-theme";

export const DEFAULT_THEME_MODE: ThemeMode = "system";

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

/** Resolve a stored value to a valid ThemeMode (fall back to default). */
export function parseThemeMode(value: string | null): ThemeMode {
  return isThemeMode(value) ? value : DEFAULT_THEME_MODE;
}

/** Resolve a mode to a concrete theme given the OS preference. */
export function resolveTheme(
  mode: ThemeMode,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  return systemPrefersDark ? "dark" : "light";
}

/** Apply a resolved theme to the document root (idempotent). */
export function applyThemeToDocument(theme: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (meta) {
    meta.setAttribute("content", theme === "dark" ? "#0d1117" : "#0f766e");
  }
}
